// The Deal Sizer's shareable link: /quote/?d=<token>. The token is the
// scenario in a fixed field order, joined by "|", base64url encoded, with
// a leading version so the format can change without breaking old links.
// Enums travel as one-letter codes. `address` is the property (never
// priced) and `zip` rides at the end so older links still decode; `ltp` is the card the sender picked, absent for the pricer's
// maximum leverage.
//
// Pure functions, tested in test/quote_link.test.js.

export const PARAM = 'd';
const VERSION = '1';

export const LINK_FIELDS = [
  'purchase_or_refi', 'deal_type', 'purchase_price', 'renovation_budget', 'arv',
  'fico', 'experience_projects', 'state', 'property_type', 'term_months', 'ltp',
  'city', 'address', 'zip'
];

const NUMERIC = ['purchase_price', 'renovation_budget', 'arv', 'fico', 'experience_projects', 'term_months', 'ltp'];

const CODES = {
  purchase_or_refi: { purchase: 'p', refinance: 'r', cash_out: 'c' },
  deal_type: { rtl: 'f', guc: 'g', dscr: 'd' },
  property_type: { single_family: 's', two_to_four: 'm', condo: 'c', townhouse: 't' }
};

function invert(map) {
  return Object.keys(map).reduce(function(acc, key) { acc[map[key]] = key; return acc; }, {});
}
const NAMES = Object.keys(CODES).reduce(function(acc, field) { acc[field] = invert(CODES[field]); return acc; }, {});

function blank(v) {
  return v === '' || v === null || v === undefined;
}

// One field as it travels: enum code, bare number, or trimmed text with
// the separator escaped so a "|" in an address cannot shift the fields.
function encodeField(name, value) {
  if (blank(value)) return '';
  if (CODES[name]) return CODES[name][String(value)] || '';
  if (NUMERIC.indexOf(name) !== -1) {
    const cleaned = String(value).replace(/[$,\s]/g, '');
    return cleaned === '' || Number.isNaN(Number(cleaned)) ? '' : cleaned;
  }
  return String(value).trim().replace(/[|%]/g, encodeURIComponent);
}

// One field back as the form wants it. Unknown codes and bad numbers are
// dropped rather than guessed.
function decodeField(name, raw) {
  if (blank(raw)) return undefined;
  if (NAMES[name]) return NAMES[name][raw];
  if (NUMERIC.indexOf(name) !== -1) return Number.isNaN(Number(raw)) ? undefined : raw;
  try { return decodeURIComponent(raw); } catch (e) { return raw; }
}

function toBase64Url(text) {
  const bytes = new TextEncoder().encode(text);
  let binary = '';
  bytes.forEach(function(b) { binary += String.fromCharCode(b); });
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function fromBase64Url(token) {
  const padded = token.replace(/-/g, '+').replace(/_/g, '/') + '==='.slice((token.length + 3) % 4);
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new TextDecoder().decode(bytes);
}

// values: the form's fields by name; ltp: the selected card's requested
// leverage or null. Returns the token, or "" when nothing is filled in.
export function toToken(values, ltp) {
  const parts = LINK_FIELDS.map(function(name) {
    return encodeField(name, name === 'ltp' ? ltp : values[name]);
  });
  if (!parts.some(Boolean)) return '';
  while (parts.length && parts[parts.length - 1] === '') parts.pop();
  return toBase64Url([VERSION].concat(parts).join('|'));
}

// The fields a token carries, as strings, plus `ltp` as a number. A token
// that does not decode, or is from another version, yields nothing.
export function fromToken(token) {
  let text;
  try { text = fromBase64Url(String(token || '')); } catch (e) { return {}; }
  const parts = text.split('|');
  if (parts.shift() !== VERSION) return {};
  const values = {};
  LINK_FIELDS.forEach(function(name, i) {
    const value = decodeField(name, parts[i]);
    if (value !== undefined) values[name] = name === 'ltp' ? Number(value) : value;
  });
  return values;
}

export function toQuery(values, ltp) {
  const token = toToken(values, ltp);
  return token ? PARAM + '=' + token : '';
}

// search: location.search (with or without "?").
export function fromQuery(search) {
  const params = new URLSearchParams(String(search || '').replace(/^\?/, ''));
  return fromToken(params.get(PARAM));
}

// True when the query carries a scenario field, i.e. the page was opened
// from a shared link rather than fresh.
export function isShared(search) {
  const values = fromQuery(search);
  return Object.keys(values).some(function(name) { return name !== 'ltp'; });
}

// The full link for the address bar and the clipboard.
export function shareUrl(base, values, ltp) {
  const query = toQuery(values, ltp);
  return query ? base + '?' + query : base;
}
